import React, { useEffect, useRef, useState } from 'react';
import 'cropperjs/dist/cropper.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-h5-audio-player/lib/styles.css';
import "react-image-crop/dist/ReactCrop.css";
import { FileText, X } from 'lucide-react';
import AddPostContent, { AddPostContentHandle } from './add-post-content';
import { useTranslation } from 'react-i18next';
import { useMediaEditor } from '../../../hooks/useMediaEditor';
import MediaEditor from '../../media-editor';
import { listDrafts } from '../../../hooks/useDraft';

interface FilePreview {
  file: File;
  src: string;
  type?: string;
}

interface AddPostProps {
  id_community?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AddPost: React.FC<AddPostProps> = ({ id_community, open, setOpen }) => {
  const closeRef   = useRef<HTMLButtonElement | null>(null);
  const openRef    = useRef<HTMLButtonElement | null>(null);
  const modalRef   = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<AddPostContentHandle | null>(null);

  const [selectedFiles, setSelectedFiles] = useState<FilePreview[]>([]);
  const { t } = useTranslation();

  const editor = useMediaEditor();

  // Always read live so the button appears as soon as drafts exist
  const draftCount = listDrafts().length;

  useEffect(() => {
    if (open) openRef.current?.click();
    else      closeRef.current?.click();
  }, [open]);

  useEffect(() => {
    const el = document.getElementById(`modal-post${id_community ? '-community' : ''}`);
    if (!el) return;
    const handleClose = () => setOpen(false);
    el.addEventListener('hidden.bs.modal', handleClose);
    return () => el.removeEventListener('hidden.bs.modal', handleClose);
  }, [open]);

  const handleEditImage = (file: File, index: number) => {
    closeRef.current?.click();

    editor.open(file, {
      onConfirm: (editedFile: any) => {
        const updatedFiles = [...selectedFiles];
        updatedFiles[index] = {
          file: editedFile,
          src:  URL.createObjectURL(editedFile),
          type: 'image',
        };
        setSelectedFiles(updatedFiles);
        openRef.current?.click();
      },
      onCancel: () => {
        openRef.current?.click();
      },
    });
  };

  return (
    <>
      <div className="col-10">
        <div
          className="modal fade"
          id={`modal-post${id_community ? '-community' : ''}`}
          aria-hidden="true"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog cover-mobile">
            <div id="username-suggestions" />
            <div className="modal-content" ref={modalRef}>
              <div className="ap-modal-header">
                <h3 className="foc-pc-title" id={`modal-post${id_community ? '-community' : ''}`}>
                  {t('post.title')}
                </h3>

                {draftCount > 0 && (
                  <button
                    type="button"
                    className="ap-drafts-btn"
                    onClick={() => contentRef.current?.openDrafts()}
                  >
                    <FileText size={14} />
                    {t('post.draft.myDrafts')} ({draftCount})
                  </button>
                )}

                <button
                  type="button"
                  className="foc-pc-close"
                  aria-label="Close"
                  onClick={() => contentRef.current?.requestClose()}
                >
                  <X size={18} />
                </button>

                {/* hidden Bootstrap dismiss trigger — called programmatically by requestClose */}
                <button
                  ref={closeRef}
                  className="d-none"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />

                <button
                  className="d-none"
                  ref={openRef}
                  data-bs-toggle="modal"
                  data-bs-target={`#modal-post${id_community ? '-community' : ''}`}
                />
              </div>

              <div
                className="modal-body"
                style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0, minHeight: 150 }}
              >
                <AddPostContent
                  ref={contentRef}
                  modalRef={modalRef}
                  setOpen={setOpen}
                  closeRef={closeRef}
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                  onEditImage={handleEditImage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {editor.isOpen && <MediaEditor {...editor.props} />}
    </>
  );
};

export default AddPost;